import React, { startTransition, useState } from "react"
import ReactPlayer from "react-player/lazy"
import * as styles from "./masthead.module.scss"
import { MastheadPropsType, CheapestRateType, ContentType } from "./types"
import { ImageMediaType } from "../../../../types"
import { animateJSX } from "../../../../utils/animation"
import Slider from "react-slick"
import Link from "../../link"
import useIsMobile from "../../../is_mobile"
import { useCustomEventListener } from "react-custom-events"
import { AvailabilityDataType } from "../rooms/types"
import { scrollToElement } from "../../../../utils/viewport"
import { ErrorsType } from "../../form/types"
import { tagExists } from "../../../../utils/hotel"
import { pricePerNight } from "../../../../utils/booking_engine"
import { opentableLink } from "../../../../utils/url"
import { useTransition } from "transition-hook"
import { FormType } from "../../../../utils/forms"
import SVG from "../../svg"

const Title = React.lazy(() => import("../../title"))
const Icon = React.lazy(() => import("../../icon"))
const Image = React.lazy(() => import("../../image"))
const PrevArrow: any = React.lazy(() => import("../../../slick/prev-arrow"))
const NextArrow: any = React.lazy(() => import("../../../slick/next-arrow"))
const Modal: any = React.lazy(() => import("../../../modal"))
const Spinner: any = React.lazy(() => import("../../../spinner"))
const ButtonElement: any = React.lazy(() => import("../../button"))
const Button: any = React.lazy(() => import("../../../button"))
const Form = React.lazy(() => import("../../form"))

const MastHead = ({ data, ...props }: MastheadPropsType) => {
  const isMobile = useIsMobile()
  const isHotelPage = () => tagExists(props.pageParams.tags, "hotel-profile")
  const [form, setForm] = useState<FormType | null>(data.form)
  const [errors, setErrors] = useState<ErrorsType>({})
  const [rate, setRate] = useState<CheapestRateType | null>(null)
  const [showRateDesc, setShowRateDesc] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [modal, setModal] = useState<boolean>(false)
  const [content, setContent] = useState<ContentType>(data.content)
  const rateState = useTransition(!!rate, 300)
  const priceBtnState = useTransition(!rate && !loading && isHotelPage(), 300)
  const showRateDescState = useTransition(showRateDesc, 300)

  useCustomEventListener("loading-availability", () => {
    startTransition(() => {
      setLoading(true)
      setRate(null)
    })
  })

  useCustomEventListener(
    "render-availability",
    (data: AvailabilityDataType) => {
      startTransition(() => {
        setLoading(false)
        // Display Rates only masthead just for Hotel pages
        if (data.hotels?.length && isHotelPage()) {
          setRate({
            name: data.hotels[0].rates[0][0].rate_name,
            description: data.hotels[0].rates[0][0].rate_description,
            cheapestRate: pricePerNight(
              data.hotels[0].cheapest_rate_total,
              data.hotels[0].nights
            ),
            currency: data.hotels[0].currency,
          })
          // Expand Rate description when Rate Code is defined
          if (
            data.searchParams.rate_codes.length ||
            data.searchParams.promo_codes.length
          ) {
            setShowRateDesc(true)
          }
        }
      })
    }
  )
  return (
    <>
      <section
        className={`masthead ${styles.section}${
          !content.videoURL ? " img" : ""
        } ${props.className ?? ""}`}
      >
        {!!content.image && (
          <div>
            <Image
              className={`mast-img${
                content.imageRatio.desktop ? " ratio" : ""
              }${content.imageRatio.mobile ? " mobile_ratio" : ""}`}
              {...content.image}
              style={{
                paddingBottom: isMobile
                  ? content.imageRatio.mobile
                  : content.imageRatio.desktop,
              }}
            />
          </div>
        )}
        {!!content.videoURL && (
          <div className={`${content.image ? "overlap-video" : ""}`}>
            <ReactPlayer
              key={props.pk + "-video"}
              loop={true}
              playing={true}
              controls={false}
              playsinline={true}
              volume={1}
              muted={true}
              width="100%"
              height="100%"
              url={content.videoURL}
            />
          </div>
        )}
        {!!content.images && (
          <div className={`carousel`}>
            <Slider
              {...{
                draggable: true,
                focusOnSelect: true,
                dots: false,
                infinite: true,
                centerMode: true,
                centerPadding: "0",
                speed: 500,
                slidesToShow: 3,
                slidesToScroll: 1,
                vertical: true,
                verticalSwiping: true,
                prevArrow: <PrevArrow icon="angle-up" />,
                nextArrow: <NextArrow icon="angle-down" />,
                responsive: [
                  {
                    breakpoint: 1407,
                    settings: {
                      slidesToShow: 2,
                      slidesToScroll: 1,
                      vertical: true,
                      verticalSwiping: true,
                      prevArrow: <PrevArrow icon="angle-up" />,
                      nextArrow: <NextArrow icon="angle-down" />,
                    },
                  },
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 1,
                      slidesToScroll: 1,
                      vertical: false,
                      verticalSwiping: false,
                      prevArrow: <PrevArrow icon="angle-left" />,
                      nextArrow: <NextArrow icon="angle-right" />,
                    },
                  },
                ],
                afterChange: function (currentSlide) {
                  if (!isMobile) {
                    setTimeout(
                      () =>
                        startTransition(() =>
                          setContent({
                            ...content,
                            image: (content.images as any)[currentSlide],
                          })
                        ),
                      200
                    )
                  }
                },
              }}
            >
              {content.images.map((image: ImageMediaType, k: number) => (
                <div key={`matheadImages${k}`}>
                  <Image
                    {...image}
                    className={`${
                      isMobile && content.imageRatio.mobile
                        ? "mobile_ratio"
                        : ""
                    }`}
                    style={
                      isMobile
                        ? { paddingBottom: content.imageRatio.mobile }
                        : undefined
                    }
                  />
                </div>
              ))}
            </Slider>
          </div>
        )}
        {!!content.shadowBox && (
          <div className="shadow-box">
            <SVG {...content.shadowBox.file} />
            <Title {...content.shadowBox.title} />
          </div>
        )}
        <div
          className={`bottom${content.subtitle ? " multi-title" : ""}${
            props.pageParams?.tags?.hasOwnProperty("hotel-wedding")
              ? " wedding"
              : ""
          }`}
        >
          {!!content.title && (
            <Title
              key={`sec${props.pk}-title`}
              className="title"
              {...content.title}
            />
          )}
          {!!content.subtitle && (
            <Title
              key={`sec${props.pk}-subtitle`}
              className="subtitle"
              {...content.subtitle}
            />
          )}
          {!!data.opentable && (
            <div className="opentable">
              <a target="_blank" href={opentableLink(data.opentable)}>
                <Button className="btn7">Reserve Now</Button>
              </a>
              <span>
                <Icon icon={{ icon: "opentable", group: "VIB" }} />
              </span>
            </div>
          )}
          {!!content.button && (
            <ButtonElement
              className="button btn6"
              {...content.button}
              onClick={() => startTransition(() => setModal(true))}
            />
          )}
          {/* Shows lowest rate on Masthead only on Hotel page */}
          {rateState.shouldMount && !!rate && (
            <div className={`rate ${animateJSX(rateState)}`}>
              <div className="rate-details">
                <h4
                  className="rate-bg"
                  onClick={() => startTransition(() => setShowRateDesc(true))}
                >
                  {rate.name}{" "}
                  <Icon color="white" icon={{ icon: "info", group: "VIB" }} />
                </h4>
                {showRateDescState.shouldMount && (
                  <div
                    className={`rate-desc rate-bg list1  ${animateJSX(
                      showRateDescState
                    )}`}
                  >
                    <Button
                      onClick={() =>
                        startTransition(() => setShowRateDesc(false))
                      }
                    >
                      <Icon
                        color="white"
                        icon={{ icon: "times", group: "FAS" }}
                      />
                    </Button>
                    <h4>{rate.name}</h4>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: rate.description,
                      }}
                    ></div>
                  </div>
                )}
              </div>
              <div className="price" onClick={() => scrollToElement("rooms")}>
                <div>BOOK A ROOM</div>
                <div>
                  <span>FROM</span>
                  <strong>${rate.cheapestRate}</strong>
                  <span>{rate.currency}/NIGHT</span>
                </div>
              </div>
            </div>
          )}
          {/* Show Price button when there are no available rates on Hotel page */}
          {priceBtnState.shouldMount && (
            <div className={`priceBtn ${animateJSX(priceBtnState)}`}>
              <Button className="btn6" onClick={() => scrollToElement("rooms")}>
                GET PRICE
              </Button>
            </div>
          )}
          {!!content.submenu && (
            <ul className="submenu">
              {content.submenu.links.map((link, k: number) => (
                <li
                  key={`masthead-submenu-${k}`}
                  className={
                    (link.group &&
                      props.pageParams.tags.hasOwnProperty(link.group)) ||
                    link.data?.active
                      ? "active"
                      : ""
                  }
                >
                  <Link
                    {...link}
                    onClick={() => {
                      if (!link.page_slug && !link.external_link && link.name) {
                        scrollToElement(link.name)
                      }
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      {!!content.button && !!form && (
        <Modal
          modalId={"main"}
          show={modal}
          setShowModal={(_: string, show: boolean) =>
            startTransition(() => setModal(show))
          }
        >
          <div className="modalForm">
            {loading ? (
              <Spinner color="#00b5ee" />
            ) : (
              <Form
                form={form}
                setForm={setForm}
                errors={errors}
                setErrors={setErrors}
                beforeSubmit={() => startTransition(() => setLoading(true))}
                afterSubmit={() =>
                  startTransition(() => {
                    setLoading(false)
                    setModal(false)
                  })
                }
              />
            )}
          </div>
        </Modal>
      )}
    </>
  )
}
export default MastHead
